import { useState } from "react";
import { motion } from "motion/react";

export interface ReverseFormData {
  // 물건 기본 정보
  propertyType: string;
  exclusiveArea: number;       // 전용면적 (m²)
  buildingAge: number;         // 건물 연식 (년)

  // 핵심: 급매 시세
  acuteMarketPrice: number;    // V: 급매 시세 (최저 호가 기준)
  courtAppraisalPrice: number; // 법원 감정가 (참고용)
  minimumBidPrice: number;     // 최저 입찰가 (참고용)

  // 목표 마진
  targetMargin: number;        // M: 목표 순수익 (절대금액)
  targetROI: number;           // 목표 ROI (%) — M과 연동
  marginMode: "amount" | "roi"; // 입력 모드 선택

  // 권리 인수금
  takeoverAmount: number;      // R: 인수 권리금액

  // 점유자
  occupantType: string;

  // 세금/금융
  homeCount: number;
  loanAmount: number;
  loanRate: number;
  loanDuration: number;

  // 기타 비용
  unpaidMgmtFee: number;
  brokerageFee: number;        // 중개수수료

  // 추가 직접 입력 비용
  extraCost: number;
}

interface ReverseFormProps {
  data: ReverseFormData;
  onChange: (data: ReverseFormData) => void;
}

export const defaultReverseFormData: ReverseFormData = {
  propertyType: "아파트",
  exclusiveArea: 85,
  buildingAge: 15,
  acuteMarketPrice: 380000000,
  courtAppraisalPrice: 420000000,
  minimumBidPrice: 294000000,
  targetMargin: 50000000,
  targetROI: 15,
  marginMode: "amount",
  takeoverAmount: 0,
  occupantType: "없음",
  homeCount: 1,
  loanAmount: 150000000,
  loanRate: 4.5,
  loanDuration: 6,
  unpaidMgmtFee: 1500000,
  brokerageFee: 1900000,
  extraCost: 0,
};

export function getDefaultReverseFormData(): ReverseFormData {
  return { ...defaultReverseFormData };
}

function NumberInput({
  value,
  onChange,
  suffix,
  placeholder,
  highlighted,
}: {
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  placeholder?: string;
  highlighted?: boolean;
}) {
  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        value={value > 0 ? value.toLocaleString() : ""}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9]/g, "");
          onChange(raw ? parseInt(raw, 10) : 0);
        }}
        className={`w-full pl-4 pr-12 py-2.5 text-sm text-right border rounded-lg bg-white focus:outline-none transition-colors font-medium ${
          highlighted
            ? "border-[#C4956A] focus:border-[#A07850] ring-2 ring-[#C4956A]/20"
            : "border-[#E8E5E0] focus:border-[#C4956A]"
        }`}
        placeholder={placeholder || "0"}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#8C8780] pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
}

export default function ReverseForm({ data, onChange }: ReverseFormProps) {
  const [expandedTip, setExpandedTip] = useState<string | null>(null);
  const update = (patch: Partial<ReverseFormData>) => onChange({ ...data, ...patch });

  const toggleTip = (key: string) => setExpandedTip(expandedTip === key ? null : key);

  return (
    <div className="space-y-5">
      {/* Track B 모드 알림 */}
      <div className="bg-gradient-to-r from-[#1C1917] to-[#3A2E28] rounded-xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#C4956A]/20 flex items-center justify-center flex-shrink-0">
          <i className="ri-flashlight-line text-[#C4956A]"></i>
        </div>
        <div>
          <p className="text-sm font-bold text-white mb-0.5">시세 역산법 (Top-Down Pricing)</p>
          <p className="text-xs text-[#B8B3AE] leading-relaxed">
            감정가가 아닌 <span className="text-[#C4956A] font-medium">실제 팔 수 있는 급매 시세</span>에서 시작해
            목표 수익 · 비용 · 리스크를 차감하여 <span className="text-white font-medium">투찰 상한가</span>를 역산합니다
          </p>
        </div>
      </div>

      {/* Section 1: 물건 기본 정보 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">1</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">물건 기본 정보</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">물건 종류</label>
            <div className="flex gap-2 flex-wrap">
              {["아파트", "다세대/연립", "오피스텔", "단독주택", "상가"].map((t) => (
                <button
                  key={t}
                  onClick={() => update({ propertyType: t })}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                    data.propertyType === t
                      ? "bg-[#1C1917] text-white border-[#1C1917] font-medium"
                      : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">전용면적 (m²)</label>
              <NumberInput value={data.exclusiveArea} onChange={(v) => update({ exclusiveArea: v })} suffix="m²" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">건물 연식 (년)</label>
              <NumberInput value={data.buildingAge} onChange={(v) => update({ buildingAge: v })} suffix="년" />
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: 핵심 — 급매 시세 (가장 중요한 섹션) */}
      <div className="bg-white rounded-xl border-2 border-[#C4956A]/40 p-5 md:p-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-[#C4956A] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">2</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">시세 정보</h3>
          <span className="px-2 py-0.5 text-[10px] font-semibold bg-[#C4956A]/10 text-[#C4956A] rounded-full ml-auto whitespace-nowrap">
            핵심 입력값
          </span>
        </div>
        <p className="text-[11px] text-[#8C8780] mb-4">
          역산의 출발점 V — 법원 감정가가 아닌 <strong className="text-[#1C1917]">실제 매각 가능한 급매가</strong>를 기준으로 입력하세요
        </p>
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <label className="text-xs font-medium text-[#5C5650]">급매 시세 (V)</label>
              <button
                onClick={() => toggleTip("acute")}
                className="w-4 h-4 rounded-full bg-[#C4956A]/20 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-question-line text-[#C4956A] text-[9px]"></i>
              </button>
            </div>
            {expandedTip === "acute" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mb-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800 leading-relaxed"
              >
                국토부 실거래가 기준으로 최근 3개월 내 동일 단지/면적 급매 최저 거래가를 기준으로 입력하세요.
                KB부동산 하한가 또는 네이버 최저 호가도 활용할 수 있습니다.
              </motion.div>
            )}
            <NumberInput
              value={data.acuteMarketPrice}
              onChange={(v) => update({ acuteMarketPrice: v })}
              suffix="원"
              highlighted
            />
            <p className="text-[10px] text-[#B8B3AE] mt-1">국토부·KB 기준 인근 급매 최저가</p>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-dashed border-[#E8E5E0]">
            <div>
              <label className="block text-xs font-medium text-[#B8B3AE] mb-1.5">법원 감정가 (참고)</label>
              <NumberInput
                value={data.courtAppraisalPrice}
                onChange={(v) => update({ courtAppraisalPrice: v })}
                suffix="원"
              />
              <p className="text-[10px] text-[#B8B3AE] mt-1">참고만 — 계산에 미반영</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#B8B3AE] mb-1.5">최저 입찰가 (참고)</label>
              <NumberInput
                value={data.minimumBidPrice}
                onChange={(v) => update({ minimumBidPrice: v })}
                suffix="원"
              />
              <p className="text-[10px] text-[#B8B3AE] mt-1">마지노선 비교용</p>
            </div>
          </div>
          {data.courtAppraisalPrice > 0 && data.acuteMarketPrice > 0 && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
              data.acuteMarketPrice < data.courtAppraisalPrice
                ? "bg-amber-50 border border-amber-200 text-amber-700"
                : "bg-emerald-50 border border-emerald-200 text-emerald-700"
            }`}>
              <i className={`${data.acuteMarketPrice < data.courtAppraisalPrice ? "ri-error-warning-line" : "ri-checkbox-circle-line"}`}></i>
              <span>
                {data.acuteMarketPrice < data.courtAppraisalPrice
                  ? `급매가가 감정가보다 ${((1 - data.acuteMarketPrice / data.courtAppraisalPrice) * 100).toFixed(1)}% 낮음 — 역산 효과 극대화`
                  : "급매가가 감정가 이상 — 시세 재확인 권장"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Section 3: 목표 마진 (M) */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">3</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">목표 마진 (M)</h3>
          <span className="text-[10px] text-[#8C8780] ml-1">— 확보하고자 하는 최소 순수익</span>
        </div>
        {/* 입력 모드 전환 */}
        <div className="flex gap-1 p-1 bg-[#F5F2EE] rounded-lg mb-4">
          {([{ key: "amount", label: "금액으로 입력" }, { key: "roi", label: "ROI(%)로 입력" }] as const).map((m) => (
            <button
              key={m.key}
              onClick={() => update({ marginMode: m.key })}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                data.marginMode === m.key
                  ? "bg-white text-[#1C1917] shadow-sm"
                  : "text-[#8C8780] hover:text-[#5C5650]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {data.marginMode === "amount" ? (
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">목표 순수익</label>
            <NumberInput
              value={data.targetMargin}
              onChange={(v) => {
                const roi = data.acuteMarketPrice > 0 ? Math.round((v / data.acuteMarketPrice) * 100) : 0;
                update({ targetMargin: v, targetROI: roi });
              }}
              suffix="원"
            />
            {data.acuteMarketPrice > 0 && data.targetMargin > 0 && (
              <p className="text-[10px] text-[#C4956A] mt-1">
                급매시세 대비 ROI: {((data.targetMargin / data.acuteMarketPrice) * 100).toFixed(1)}%
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">목표 ROI (%)</label>
            <NumberInput
              value={data.targetROI}
              onChange={(v) => {
                const margin = Math.round(data.acuteMarketPrice * (v / 100));
                update({ targetROI: v, targetMargin: margin });
              }}
              suffix="%"
            />
            {data.targetROI > 0 && (
              <p className="text-[10px] text-[#C4956A] mt-1">
                환산 금액: {Math.round(data.acuteMarketPrice * (data.targetROI / 100)).toLocaleString()}원
              </p>
            )}
          </div>
        )}
      </div>

      {/* Section 4: 권리 & 점유 (R) */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">4</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">권리 & 점유 (R)</h3>
        </div>
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <label className="text-xs font-medium text-[#5C5650]">인수 권리금액 (R)</label>
              <button
                onClick={() => toggleTip("takeover")}
                className="w-4 h-4 rounded-full bg-rose-100 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-question-line text-rose-500 text-[9px]"></i>
              </button>
            </div>
            {expandedTip === "takeover" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mb-2 bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs text-rose-800 leading-relaxed"
              >
                대항력 있는 임차인의 미상환 보증금, 유치권 설정액, 선순위 가처분 등 낙찰 후 반드시 인수해야 하는 금액을 입력하세요.
                이 금액은 낙찰 가격에 추가되는 실질 비용입니다.
              </motion.div>
            )}
            <NumberInput
              value={data.takeoverAmount}
              onChange={(v) => update({ takeoverAmount: v })}
              suffix="원"
            />
            <p className="text-[10px] text-[#B8B3AE] mt-1">대항력 있는 임차인 보증금, 유치권, 선순위 가처분 등</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">점유자 유형</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "없음", label: "점유자 없음" },
                { value: "debtor", label: "채무자" },
                { value: "tenant_no_rights", label: "임차인 (대항력 없음)" },
                { value: "tenant_with_rights", label: "임차인 (대항력 있음)" },
                { value: "lien", label: "유치권자" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => update({ occupantType: opt.value })}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                    data.occupantType === opt.value
                      ? "bg-[#1C1917] text-white border-[#1C1917] font-medium"
                      : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section 5: 세금 & 금융 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">5</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">세금 & 금융 (C)</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">보유 주택 수</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 0, label: "무주택자" },
                { value: 1, label: "1주택자" },
                { value: 2, label: "2주택자" },
                { value: 3, label: "3주택 이상" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => update({ homeCount: opt.value })}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                    data.homeCount === opt.value
                      ? "bg-[#1C1917] text-white border-[#1C1917] font-medium"
                      : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">대출 금액</label>
              <NumberInput value={data.loanAmount} onChange={(v) => update({ loanAmount: v })} suffix="원" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">대출 금리</label>
              <NumberInput value={data.loanRate} onChange={(v) => update({ loanRate: v })} suffix="%" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">소요 기간</label>
              <NumberInput value={data.loanDuration} onChange={(v) => update({ loanDuration: v })} suffix="개월" />
            </div>
          </div>
        </div>
      </div>

      {/* Section 6: 기타 비용 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">6</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">기타 비용 (C)</h3>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">미납 관리비</label>
              <NumberInput value={data.unpaidMgmtFee} onChange={(v) => update({ unpaidMgmtFee: v })} suffix="원" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">중개수수료</label>
              <NumberInput value={data.brokerageFee} onChange={(v) => update({ brokerageFee: v })} suffix="원" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">기타 추가 비용</label>
            <NumberInput value={data.extraCost} onChange={(v) => update({ extraCost: v })} suffix="원" />
            <p className="text-[10px] text-[#B8B3AE] mt-1">등기 비용, 인테리어, 기타 직접 비용 등</p>
          </div>
        </div>
      </div>

      {/* 리셋 버튼 */}
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => onChange({ ...defaultReverseFormData })}
        className="w-full py-2.5 text-sm font-medium text-[#8C8780] border border-[#E8E5E0] rounded-lg hover:border-[#C4956A] hover:text-[#C4956A] transition-all cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line mr-1.5"></i>
        초기값으로 리셋
      </motion.button>
    </div>
  );
}